import { useCallback, useEffect, useMemo, useState } from 'react'
import {
	createUser,
	listAuditEvents,
	listPositions,
	listUsers,
	logPageVisit,
	setUserActive,
	updateUserProfile,
	type AuditEvent,
	type AuthUser,
	type ManagedUser,
} from '../api'
import { compareUsers, type UserSortKey } from '../lib/userSort'

type Screen = 'boardstats' | 'accounts'

export function useAccountsPortal(
	screen: Screen,
	user: AuthUser | null,
	reportError: (e: unknown) => void,
	dismissError: () => void
) {
	const [rows, setRows] = useState<ManagedUser[]>([])
	const [events, setEvents] = useState<AuditEvent[]>([])
	const [newLogin, setNewLogin] = useState('')
	const [newFullName, setNewFullName] = useState('')
	const [newPosition, setNewPosition] = useState('')
	const [newPassword, setNewPassword] = useState('')
	const [newBoardstats, setNewBoardstats] = useState(true)
	const [newManage, setNewManage] = useState(false)
	const [newSuper, setNewSuper] = useState(false)
	const [createModalOpen, setCreateModalOpen] = useState(false)
	const [activityUser, setActivityUser] = useState<ManagedUser | null>(null)
	const [userEvents, setUserEvents] = useState<AuditEvent[]>([])
	const [userEventsLoading, setUserEventsLoading] = useState(false)
	const [positionOptions, setPositionOptions] = useState<string[]>([])
	const [editProfileModalUser, setEditProfileModalUser] = useState<ManagedUser | null>(null)
	const [editFullName, setEditFullName] = useState('')
	const [editPosition, setEditPosition] = useState('')
	const [passwordValue, setPasswordValue] = useState('')
	const [passwordConfirmValue, setPasswordConfirmValue] = useState('')
	const [profileSaving, setProfileSaving] = useState(false)
	const [userSortKey, setUserSortKey] = useState<UserSortKey>('login')
	const [userSortDir, setUserSortDir] = useState<'asc' | 'desc'>('asc')
	const [activeToggling, setActiveToggling] = useState(false)

	const loadUsers = useCallback(async () => {
		try {
			setRows(await listUsers())
		} catch (e) {
			reportError(e)
		}
	}, [reportError])

	const loadEvents = useCallback(async () => {
		try {
			setEvents(await listAuditEvents(1000))
		} catch (e) {
			reportError(e)
		}
	}, [reportError])

	const loadPositions = useCallback(async () => {
		try {
			setPositionOptions(await listPositions())
		} catch (e) {
			reportError(e)
		}
	}, [reportError])

	const openUserActivity = useCallback(
		async (target: ManagedUser) => {
			dismissError()
			setActivityUser(target)
			setUserEvents([])
			setUserEventsLoading(true)
			try {
				setUserEvents(await listAuditEvents(1000, target.login))
			} catch (e) {
				reportError(e)
				setActivityUser(null)
			} finally {
				setUserEventsLoading(false)
			}
		},
		[dismissError, reportError]
	)

	const closeActivityModal = useCallback(() => {
		setActivityUser(null)
		setUserEvents([])
		setEditProfileModalUser(null)
		setEditFullName('')
		setEditPosition('')
		setPasswordValue('')
		setPasswordConfirmValue('')
	}, [])

	const closeEditProfileModal = useCallback(() => {
		setEditProfileModalUser(null)
		setPasswordValue('')
		setPasswordConfirmValue('')
	}, [])

	const toggleAccountActive = useCallback(async () => {
		if (!activityUser) {
			return
		}
		dismissError()
		const next = !activityUser.is_active
		setActiveToggling(true)
		try {
			await setUserActive(activityUser.id, next)
			setActivityUser({ ...activityUser, is_active: next })
			await loadUsers()
		} catch (e) {
			reportError(e)
		} finally {
			setActiveToggling(false)
		}
	}, [activityUser, dismissError, loadUsers, reportError])

	useEffect(() => {
		if (screen === 'accounts' && user?.is_super_admin) {
			void loadUsers()
			void loadEvents()
			void loadPositions()
			void logPageVisit('auth_accounts')
		}
	}, [loadEvents, loadPositions, loadUsers, screen, user])

	const submitCreate = useCallback(async () => {
		dismissError()
		try {
			await createUser({
				login: newLogin,
				full_name: newFullName,
				position: newPosition,
				password: newPassword,
				permissions: newBoardstats ? ['boardstats'] : [],
				can_manage_accounts: newManage,
				is_super_admin: newSuper,
			})
			setNewLogin('')
			setNewFullName('')
			setNewPosition('')
			setNewPassword('')
			setNewBoardstats(true)
			setNewManage(false)
			setNewSuper(false)
			setCreateModalOpen(false)
			await loadUsers()
			await loadPositions()
		} catch (e) {
			reportError(e)
		}
	}, [dismissError, loadPositions, loadUsers, newBoardstats, newFullName, newLogin, newManage, newPassword, newPosition, newSuper, reportError])

	const submitProfileEdit = useCallback(async () => {
		if (!editProfileModalUser) {
			return
		}
		const pw = passwordValue.trim()
		const pc = passwordConfirmValue.trim()
		const wantsPw = pw !== '' || pc !== ''
		if (wantsPw && (pw === '' || pc === '' || pw !== pc)) {
			return
		}
		dismissError()
		setProfileSaving(true)
		try {
			const body: { full_name?: string; position?: string; password?: string; password_confirm?: string } = {
				full_name: editFullName,
				position: editPosition,
			}
			if (pw !== '') {
				body.password = pw
				body.password_confirm = pc
			}
			await updateUserProfile(editProfileModalUser.id, body)
			const nextName = editFullName.trim() === '' ? null : editFullName.trim()
			const nextPos = editPosition.trim() === '' ? null : editPosition.trim()
			const updated = { ...editProfileModalUser, full_name: nextName, position: nextPos }
			setActivityUser((u) => (u && u.id === updated.id ? updated : u))
			setEditProfileModalUser(null)
			setPasswordValue('')
			setPasswordConfirmValue('')
			await loadUsers()
			await loadPositions()
		} catch (e) {
			reportError(e)
		} finally {
			setProfileSaving(false)
		}
	}, [
		editFullName,
		editPosition,
		editProfileModalUser,
		loadPositions,
		loadUsers,
		passwordConfirmValue,
		passwordValue,
		dismissError,
		reportError,
	])

	const profilePasswordInvalid =
		passwordValue.trim() !== '' || passwordConfirmValue.trim() !== ''
			? passwordValue.trim() === '' ||
				passwordConfirmValue.trim() === '' ||
				passwordValue !== passwordConfirmValue
			: false

	const sortedRows = useMemo(() => {
		const copy = [...rows]
		copy.sort((a, b) => {
			const c = compareUsers(a, b, userSortKey)
			return userSortDir === 'asc' ? c : -c
		})
		return copy
	}, [rows, userSortDir, userSortKey])

	const toggleUserSort = useCallback((key: UserSortKey) => {
		if (userSortKey === key) {
			setUserSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
		} else {
			setUserSortKey(key)
			setUserSortDir(key === 'active' ? 'desc' : 'asc')
		}
	}, [userSortKey])

	const userSortableHeader = useCallback(
		(key: UserSortKey, label: string) => {
			const ind = userSortKey === key ? (userSortDir === 'asc' ? '↑' : '↓') : ''
			return (
				<th scope="col">
					<button type="button" className={`th-sort${userSortKey === key ? ' is-active' : ''}`} onClick={() => toggleUserSort(key)}>
						{label}
						{ind ? <span className="sort-ind">{ind}</span> : null}
					</button>
				</th>
			)
		},
		[toggleUserSort, userSortDir, userSortKey]
	)

	const beginEditProfile = useCallback(() => {
		if (!activityUser) {
			return
		}
		setEditProfileModalUser(activityUser)
		setEditFullName(activityUser.full_name ?? '')
		setEditPosition(activityUser.position ?? '')
		setPasswordValue('')
		setPasswordConfirmValue('')
	}, [activityUser])

	return {
		events,
		sortedRows,
		positionOptions,
		activityUser,
		userEvents,
		userEventsLoading,
		editProfileModalUser,
		editFullName,
		setEditFullName,
		editPosition,
		setEditPosition,
		passwordValue,
		setPasswordValue,
		passwordConfirmValue,
		setPasswordConfirmValue,
		createModalOpen,
		setCreateModalOpen,
		newLogin,
		setNewLogin,
		newFullName,
		setNewFullName,
		newPosition,
		setNewPosition,
		newPassword,
		setNewPassword,
		newBoardstats,
		setNewBoardstats,
		newManage,
		setNewManage,
		newSuper,
		setNewSuper,
		profileSaving,
		profilePasswordInvalid,
		activeToggling,
		userSortableHeader,
		openUserActivity,
		closeActivityModal,
		closeEditProfileModal,
		toggleAccountActive,
		submitCreate,
		submitProfileEdit,
		beginEditProfile,
	}
}
